type ParamType = 'string' | 'datetime';

interface MessageConfig {
  titleKey: string;
  messageKey: string;
  actionLabelKey: string;
  icon: string;
  action?: { labelKey: string; route: string };
  paramTypes?: Record<string, ParamType>;
}

const messageConfig: Record<string, MessageConfig> = {
  google_token_refresh_failed: {
    titleKey: 'permanent.googleTokenRefreshFailed.title',
    messageKey: 'permanent.googleTokenRefreshFailed.message',
    actionLabelKey: 'permanent.googleTokenRefreshFailed.action',
    icon: 'heroicons:exclamation-triangle',
    action: { labelKey: 'permanent.googleTokenRefreshFailed.action', route: '/settings/google-account' },
  },
  maintenance: {
    titleKey: 'permanent.maintenance.title',
    messageKey: 'permanent.maintenance.message',
    actionLabelKey: 'permanent.maintenance.action',
    icon: 'heroicons:wrench-screwdriver',
    paramTypes: { startTime: 'datetime', endTime: 'datetime' },
  },
  feature_update: {
    titleKey: 'permanent.featureUpdate.title',
    messageKey: 'permanent.featureUpdate.message',
    actionLabelKey: 'permanent.featureUpdate.action',
    icon: 'heroicons:sparkles',
    paramTypes: { feature: 'string' },
  },
};

const defaultConfig: MessageConfig = {
  titleKey: '',
  messageKey: '',
  actionLabelKey: '',
  icon: 'heroicons:information-circle',
};

export interface EnrichedPermanentMessage {
  key: string;
  type: string;
  params: Record<string, string>;
  id: number | null;
  dismissed: boolean;
  dismissKey: string;
  titleKey: string;
  messageKey: string;
  icon: string;
  action?: { labelKey: string; route: string };
}

interface RawPermanentMessage {
  key: string;
  type: string;
  params: Record<string, string>;
  id: number | null;
  dismissed: boolean;
  actionRoute: string | null;
}

const dateFormatOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
const timeFormatOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

function formatParams(params: Record<string, string>, paramTypes: Record<string, ParamType> | undefined, locale: string): Record<string, string> {
  if (!paramTypes) return params;

  const formatted: Record<string, string> = {};

  for (const [key, value] of Object.entries(params)) {
    const pType = paramTypes[key] ?? 'string';
    if (pType === 'datetime') {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        formatted[key] = parsed.toLocaleTimeString(locale, timeFormatOptions);
        formatted[key + 'Date'] = parsed.toLocaleDateString(locale, dateFormatOptions);
        continue;
      }
    }
    formatted[key] = value;
  }

  return formatted;
}

export function usePermanentMessages() {
  const { data: session, getSession } = useAuth();
  const { locale } = useI18n();
  const api = useApi();

  const messages = computed<EnrichedPermanentMessage[]>(() => {
    const raw = (session.value?.permanentMessages ?? []) as RawPermanentMessage[];
    return raw.map((msg) => {
      const config = messageConfig[msg.key] ?? defaultConfig;
      const action = msg.actionRoute ? { labelKey: config.actionLabelKey, route: msg.actionRoute } : config.action;
      return {
        ...msg,
        params: formatParams(msg.params, config.paramTypes, locale.value),
        dismissKey: msg.id !== null ? `admin_${msg.id}` : msg.key,
        titleKey: config.titleKey,
        messageKey: config.messageKey,
        icon: config.icon,
        action,
      };
    });
  });

  const undismissedMessages = computed(() => messages.value.filter((m) => !m.dismissed));
  const dismissedMessages = computed(() => messages.value.filter((m) => m.dismissed));
  const hasMessages = computed(() => messages.value.length > 0);
  const messageCount = computed(() => messages.value.length);

  const dismiss = async (msg: EnrichedPermanentMessage) => {
    await api.post(`permanent-messages/${encodeURIComponent(msg.dismissKey)}/dismiss`);
    await getSession();
  };

  return {
    messages,
    undismissedMessages,
    dismissedMessages,
    hasMessages,
    messageCount,
    dismiss,
  };
}
